import React, { useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';

/**
 * Grouped/hierarchical view for organizing events by truck, system, or date.
 * Shows collapsible sections with counts and outlier highlighting.
 */
const GroupedView = ({ data, aggregations, summary }) => {
    const [expandedGroups, setExpandedGroups] = useState({});
    const [groupBy, setGroupBy] = useState('truck'); // 'truck', 'system', 'timeline'

    const getEventDowntime = (event) => {
        const keys = ['downtime_hours', 'lngTotal', 'contract_hours'];
        for (const key of keys) {
            if (event[key] !== undefined && event[key] !== null) {
                const num = Number(event[key]);
                if (!Number.isNaN(num)) {
                    return num;
                }
            }
        }
        return null;
    };

    const formatDowntimeHours = (hours) => {
        if (hours === null || hours === undefined) return null;
        return Number.isInteger(hours) ? `${hours}hrs` : `${hours.toFixed(1)}hrs`;
    };

    const getGroupDowntime = (events) => {
        const keys = ['downtime_hours', 'lngTotal', 'contract_hours'];
        let total = 0;
        let found = false;

        events.forEach((event) => {
            for (const key of keys) {
                if (event[key] !== undefined && event[key] !== null) {
                    const num = Number(event[key]);
                    if (!Number.isNaN(num)) {
                        total += num;
                        found = true;
                        break;
                    }
                }
            }
        });

        return { total, found };
    };

    // Group data based on selection
    const groupedData = React.useMemo(() => {
        if (!data || data.length === 0) return {};
        const groups = {};

        if (groupBy === 'truck') {
            data.forEach(event => {
                const truck = event.calUnitNo || event.Unit || event.unit || 'Unknown';
                if (!groups[truck]) groups[truck] = [];
                groups[truck].push(event);
            });
        } else if (groupBy === 'system') {
            // Use aggregations if available, otherwise categorize manually
            if (aggregations?.by_system) {
                data.forEach(event => {
                    const desc = (event.description || '').toLowerCase();
                    let assigned = false;

                    // Try to match to a known system
                    for (const system of Object.keys(aggregations.by_system)) {
                        if (desc.includes(system.toLowerCase())) {
                            if (!groups[system]) groups[system] = [];
                            groups[system].push(event);
                            assigned = true;
                            break;
                        }
                    }

                    if (!assigned) {
                        if (!groups['Other']) groups['Other'] = [];
                        groups['Other'].push(event);
                    }
                });
            }
        } else if (groupBy === 'timeline') {
            data.forEach(event => {
                const date = event.formatted_date?.split(' ')[0] || 'Unknown Date';
                if (!groups[date]) groups[date] = [];
                groups[date].push(event);
            });
        }

        return groups;
    }, [data, groupBy, aggregations]);

    if (!data || data.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                No data to group
            </div>
        );
    }

    // Sort groups by count (descending)
    const sortedGroupKeys = Object.keys(groupedData).sort((a, b) =>
        groupedData[b].length - groupedData[a].length
    );

    // Check if a group is an outlier
    const isOutlier = (groupKey) => {
        return summary?.outliers?.includes(groupKey);
    };

    // Toggle group expansion
    const toggleGroup = (key) => {
        setExpandedGroups(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    return (
        <div className="space-y-4">
            {/* Group By Selector */}
            <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Group by:</span>
                <div className="flex gap-2">
                    <button
                        onClick={() => setGroupBy('truck')}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${groupBy === 'truck'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                    >
                        Truck
                    </button>
                    <button
                        onClick={() => setGroupBy('system')}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${groupBy === 'system'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                    >
                        System
                    </button>
                    <button
                        onClick={() => setGroupBy('timeline')}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${groupBy === 'timeline'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                    >
                        Date
                    </button>
                </div>
            </div>

            {/* Grouped Items */}
            <div className="space-y-2">
                {sortedGroupKeys.map(groupKey => {
                    const events = groupedData[groupKey];
                    const isExpanded = expandedGroups[groupKey];
                    const outlier = isOutlier(groupKey);
                    const downtime = getGroupDowntime(events);

                    return (
                        <div
                            key={groupKey}
                            className={`border rounded-lg overflow-hidden transition-all ${outlier ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                                }`}
                        >
                            {/* Group Header */}
                            <button
                                onClick={() => toggleGroup(groupKey)}
                                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    {isExpanded ? (
                                        <ChevronDown className="w-5 h-5 text-gray-500" />
                                    ) : (
                                        <ChevronRight className="w-5 h-5 text-gray-500" />
                                    )}

                                    <span className="font-semibold text-gray-900">
                                        {groupKey}
                                    </span>

                                    {outlier && (
                                        <div className="flex items-center gap-1 text-red-600 text-xs">
                                            <AlertTriangle className="w-4 h-4" />
                                            <span>High Activity</span>
                                        </div>
                                    )}
                                </div>

                                <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                                    {events.length} events
                                </span>
                            </button>

                            {/* Group Content */}
                            {isExpanded && (
                                <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
                                    <div className="space-y-2">
                                        {events.map((event, idx) => {
                                            const eventDowntime = getEventDowntime(event);
                                            const formattedEventDowntime = formatDowntimeHours(eventDowntime);

                                            return (
                                                <div
                                                    key={idx}
                                                    className="bg-white p-3 rounded-lg border border-gray-200 text-sm"
                                                >
                                                    <div className="font-medium text-gray-900 mb-2">
                                                        {event.description}
                                                    </div>

                                                    <div className="text-xs text-gray-500 mb-1.5 flex items-center gap-4">
                                                        {event.work_order && event.work_order !== 'N/A' && (
                                                            <span>WO: {event.work_order}</span>
                                                        )}
                                                        {groupBy !== 'truck' && event.calUnitNo && (
                                                            <span>Unit: {event.calUnitNo}</span>
                                                        )}
                                                    </div>

                                                    <div className="text-xs text-gray-600 flex items-center justify-between mb-1.5">
                                                        <span>{event.formatted_date?.split(' ')[0] || 'Unknown Date'}</span>
                                                        <span>Downtime: {formattedEventDowntime || 'N/A'}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-2 text-xs text-gray-500 flex justify-end">
                                        Total Downtime: {downtime.found ? `${downtime.total.toFixed(1)}hrs` : 'N/A'}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Summary Stats */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                        <div className="text-blue-600 font-medium">Total Groups</div>
                        <div className="text-blue-900 text-lg font-bold">{sortedGroupKeys.length}</div>
                    </div>
                    <div>
                        <div className="text-blue-600 font-medium">Total Events</div>
                        <div className="text-blue-900 text-lg font-bold">{data.length}</div>
                    </div>
                    {summary?.outliers && summary.outliers.length > 0 && (
                        <div>
                            <div className="text-red-600 font-medium">High Activity Groups</div>
                            <div className="text-red-900 text-lg font-bold">{summary.outliers.length}</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GroupedView;
